// Generated by ts-to-zod
import { z } from "zod";

const textAlignSchema = z.union([
  z.literal("left"),
  z.literal("right"),
  z.literal("center"),
  z.literal("justify"),
  z.literal("start"),
  z.literal("end"),
  z.literal("match-parent"),
]);

const alignItemsSchema = z.union([
  z.literal("stretch"),
  z.literal("center"),
  z.literal("flex-start"),
  z.literal("flex-end"),
  z.literal("baseline"),
  z.literal("initial"),
  z.literal("inherit"),
]);

const justifyContentSchema = z.union([
  z.literal("flex-start"),
  z.literal("flex-end"),
  z.literal("center"),
  z.literal("space-between"),
  z.literal("space-around"),
  z.literal("space-evenly"),
  z.literal("initial"),
  z.literal("inherit"),
]);

const relAttributeSchema = z.union([
  z.literal("alternate"),
  z.literal("author"),
  z.literal("bookmark"),
  z.literal("external"),
  z.literal("help"),
  z.literal("license"),
  z.literal("next"),
  z.literal("nofollow"),
  z.literal("noopener"),
  z.literal("noreferrer"),
  z.literal("prev"),
  z.literal("search"),
  z.literal("tag"),
]);

const flexDirectionSchema = z.union([
  z.literal("row"),
  z.literal("row-reverse"),
  z.literal("column"),
  z.literal("column-reverse"),
]);

const hexColorSchema = z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);

const rGBColorSchema = z
  .string()
  .regex(/^rgb\((\d{1,3}),(\d{1,3}),(\d{1,3})\)$/);

const rGBAColorSchema = z
  .string()
  .regex(/^rgba\((\d{1,3}),(\d{1,3}),(\d{1,3}),(\d(\.\d+)?|1(\.0+)?)\)$/);

const colorSchema = z.union([hexColorSchema, rGBColorSchema, rGBAColorSchema]);

const unitPxSchema = z.string().regex(/^\d+px$/);

const siteElementBaseSettingsSchema = z.object({
  styles: z.object({
    align: alignItemsSchema,
    justify: justifyContentSchema,
    text: textAlignSchema,
    "m-element-margin": z.string().regex(/^\d+px \d+px \d+px \d+px$/),
    "z-index": z.number(),
    "grid-row": z.string(),
    "grid-column": z.string(),
    "m-grid-row": z.string(),
    "m-grid-column": z.string(),
  }),
});

const siteElementBasePositionSchema = z.object({
  top: z.number(),
  left: z.number(),
  width: z.number(),
  height: z.number(),
  fontSize: z.number().optional(),
});

const animationSchema = z.object({
  name: z.string(),
  type: z.string(),
});

export const siteElementGridSocialIconLinkSchema = z.object({
  svg: z.string(),
  icon: z.string(),
  link: z.string().optional(),
  url: z.string().optional(),
});

export const siteElementTypeSchema = z.union([
  z.literal("GridGallery"),
  z.literal("GridTextBox"),
  z.literal("GridShape"),
  z.literal("GridImage"),
  z.literal("GridButton"),
  z.literal("GridMap"),
  z.literal("GridVideo"),
  z.literal("GridInstagramFeed"),
  z.literal("GridSocialIcons"),
  z.literal("GridForm"),
  z.literal("GridEmbed"),
  z.literal("GridStripeButton"),
  z.literal("GridEcommerceButton"),
  z.literal("SearchBar"),
]);

export const siteElementBaseSchema = z.object({
  type: siteElementTypeSchema,
  initialElementId: z.string(),
  mobile: siteElementBasePositionSchema,
  desktop: siteElementBasePositionSchema,
  animation: animationSchema.optional(),
  settings: siteElementBaseSettingsSchema,
  aiData: z
    .record(z.unknown())
    .and(
      z.object({
        type: z.string().optional(),
      }),
    )
    .optional(),
});

export const linkTypeSchema = z.union([
  z.literal("external"),
  z.literal("download"),
  z.literal("anchored-section"),
  z.literal("email"),
  z.literal("phone"),
  z.literal("page"),
]);

export const siteElementBaseButtonSchema = siteElementBaseSchema.extend({
  href: z.string(),
  rel: relAttributeSchema,
  target: z.union([
    z.literal("_blank"),
    z.literal("_self"),
    z.literal("_parent"),
    z.literal("_top"),
  ]),
  content: z.string(),
  linkType: linkTypeSchema,
  fontColor: colorSchema,
  fontWeight: z.number(),
  borderColor: colorSchema,
  borderWidth: z.number(),
  borderRadius: z.number(),
  fontColorHover: colorSchema,
  backgroundColor: colorSchema,
  borderColorHover: colorSchema,
  backgroundColorHover: colorSchema,
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      type: z.string(),
      isFormButton: z.boolean().optional(),
    }),
  ),
  linkedPageId: z.string().optional(),
});

export const siteElementGridStripeButtonSchema =
  siteElementBaseButtonSchema.extend({
    type: z.literal("GridStripeButton"),
    priceId: z.string(),
    paymentType: z.string(),
  });

export const siteElementGridEcommerceButtonSchema =
  siteElementBaseButtonSchema.extend({
    type: z.literal("GridEcommerceButton"),
    productId: z.string(),
    productVariantId: z.string(),
  });

export const siteElementGridMapSchema = siteElementBaseSchema.extend({
  type: z.literal("GridMap"),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      src: z.string(),
    }),
  ),
});

export const siteElementGridVideoSchema = siteElementBaseSchema.extend({
  type: z.literal("GridVideo"),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      jpg: z.string(),
      src: z.string(),
      webp: z.string(),
      provider: z.union([z.literal("youtube"), z.literal("vimeo")]),
      initialSrc: z.string(),
    }),
  ),
});

export const siteElementGridTextBoxSchema = siteElementBaseSchema.extend({
  type: z.literal("GridTextBox"),
  content: z.string(),
});

export const siteElementGridShapeSchema = siteElementBaseSchema.extend({
  type: z.literal("GridShape"),
  svg: z.string(),
  color: colorSchema,
  shape: z.string(),
});

export const siteElementGridImageSchema = siteElementBaseSchema.extend({
  type: z.literal("GridImage"),
  rel: relAttributeSchema,
  href: z.string(),
  mobile: siteElementBasePositionSchema.and(
    z.object({
      borderRadius: z.string(),
    }),
  ),
  desktop: siteElementBasePositionSchema.and(
    z.object({
      borderRadius: z.string(),
    }),
  ),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      alt: z.string(),
      path: z.string(),
      origin: z.string(),
      clickAction: z.string(),
    }),
  ),
});

export const siteElementGridInstagramFeedSchema = siteElementBaseSchema.extend({
  type: z.literal("GridInstagramFeed"),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      username: z.string().optional(),
      apiVersion: z
        .union([
          z.literal("instagram_basic_display"),
          z.literal("instagram_api_with_instagram_login"),
        ])
        .optional(),
      "item-gap": unitPxSchema,
      "m-item-gap": unitPxSchema,
      "items-per-row": z.number(),
      "m-items-per-row": z.number(),
    }),
  ),
  "item-count": z.number(),
});

export const siteElementGridSocialIconsSchema = siteElementBaseSchema.extend({
  type: z.literal("GridSocialIcons"),
  useBrandColors: z.boolean(),
  links: z.array(siteElementGridSocialIconLinkSchema),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      "icon-size": unitPxSchema,
      "icon-color": colorSchema,
      "icon-spacing": justifyContentSchema,
      "icon-direction": flexDirectionSchema,
      "icon-color-hover": colorSchema,
      "space-between-icons": unitPxSchema,
    }),
  ),
});

export const siteElementGridGallerySchema = siteElementBaseSchema.extend({
  type: z.literal("GridGallery"),
  images: z.array(
    z.object({
      alt: z.string(),
      path: z.string(),
      origin: z.union([
        z.literal("assets"),
        z.literal("unsplash"),
        z.literal("other"),
      ]),
    }),
  ),
  desktop: siteElementBasePositionSchema.and(
    z.object({
      columnGap: z.number(),
      columnCount: z.number(),
    }),
  ),
  mobile: siteElementBasePositionSchema.and(
    z.object({
      columnGap: z.number(),
      columnCount: z.number(),
    }),
  ),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      layout: z.string(),
      imageClickAction: z.union([z.literal("lightbox"), z.literal("none")]),
    }),
  ),
});

export const siteElementGridEmbedSchema = siteElementBaseSchema.extend({
  type: z.literal("GridEmbed"),
  content: z.string(),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      styles: z.object({
        height: z.number(),
        heightMobile: z.number().optional(),
      }),
    }),
  ),
});

export const siteElementSearchBarSchema = siteElementBaseSchema.extend({
  type: z.literal("SearchBar"),
  placeholderText: z.string(),
  noResultsText: z.string(),
  fillColor: colorSchema,
  fillColorHover: colorSchema,
  resultItemHoverColor: colorSchema,
  textAndIconColor: colorSchema,
  textAndIconColorHover: colorSchema,
  borderColor: colorSchema,
  borderColorHover: colorSchema,
  borderRadius: z.number(),
  fontFamily: z.string(),
});

export const siteElementGridButtonSchema = siteElementBaseButtonSchema.extend({
  type: z.literal("GridButton"),
});

export const siteTypographyStylesSchema = z.object({
  "font-family": z.string(),
  "font-size": z.string(),
  "m-font-size": z.string(),
  "font-style": z.string(),
  "font-weight": z.union([
    z.literal(100),
    z.literal(200),
    z.literal(300),
    z.literal(400),
    z.literal(500),
    z.literal(600),
    z.literal(700),
    z.literal(800),
    z.literal(900),
    z.literal("100"),
    z.literal("200"),
    z.literal("300"),
    z.literal("400"),
    z.literal("500"),
    z.literal("600"),
    z.literal("700"),
    z.literal("800"),
    z.literal("900"),
    z.literal("normal"),
    z.literal("bold"),
  ]),
  "line-height": z.string(),
  "text-decoration": z.string(),
  "text-transform": z.string(),
  "letter-spacing": z.union([z.string(), z.number()]),
  color: z.string().optional(),
  "color-hover": z.string().optional(),
});

export const siteGridButtonStyleSchema = z.object({
  "padding-y": z.string(),
  "m-padding-y": z.string(),
  "padding-x": z.string(),
  "m-padding-x": z.string(),
  "font-family": z.string(),
  "font-size": z.string(),
  "m-font-size": z.string(),
  "font-style": z.string(),
  "font-weight": z.union([
    z.literal(100),
    z.literal(200),
    z.literal(300),
    z.literal(400),
    z.literal(500),
    z.literal(600),
    z.literal(700),
    z.literal(800),
    z.literal(900),
  ]),
  "line-height": z.string(),
  "text-decoration": z.string(),
  "text-transform": z.string(),
  "letter-spacing": z.string(),
  "background-color-null": z.string(),
  "background-color-null-hover": z.string(),
  "border-null": z.string(),
  "border-null-hover": z.string(),
  "border-radius": z.number(),
  "box-shadow-null": z.string(),
  "box-shadow-null-hover": z.string(),
  "box-shadow-x": z.string(),
  "box-shadow-x-hover": z.string(),
  "box-shadow-blur": z.string(),
  "box-shadow-blur-hover": z.string(),
  "box-shadow-spread": z.string(),
  "box-shadow-spread-hover": z.string(),
  "box-shadow-y": z.string(),
  "box-shadow-y-hover": z.string(),
  "box-shadow-color": z.string(),
  "box-shadow-color-hover": z.string(),
  "transition-duration": z.string(),
  "transition-timing-function": z.string(),
});

export const siteBlockTypeSchema = z.union([
  z.literal("BlockSlideshow"),
  z.literal("BlockGrid"),
  z.literal("BlockNavigation"),
  z.literal("BlockBlogList"),
  z.literal("BlockBlogHeader"),
  z.literal("BlockEcommerceProduct"),
  z.literal("BlockEcommerceModal"),
  z.literal("BlockEcommerceProductList"),
  z.literal("BlockLayout"),
  z.literal("BlockImageSlideshow"),
  z.literal("BlockStickyBar"),
]);

export const siteBackgroundTypeSchema = z.union([
  z.literal("path"),
  z.literal("origin"),
  z.literal("color"),
  z.literal("gradient"),
  z.literal("image"),
  z.literal("video"),
]);

export const siteBackgroundGradientSchema = z.object({
  angle: z.number().min(0).max(360),
  isAnimated: z.boolean(),
  colors: z.array(
    z.object({
      value: z.string(),
    }),
  ),
});

export const siteEcommerceShoppingCartSchema = z.record(z.unknown()).and(
  z.object({
    translations: z.record(z.string()),
    lang: z.string().optional(),
  }),
);

export const siteEcommerceSeoChangesSchema = z.object({
  id: z.string(),
  seo_settings: z.object({
    title: z.string().optional(),
    noindex: z.boolean().optional(),
    keywords: z.array(z.string()).optional(),
    description: z.string().optional(),
    focusKeyword: z.string().optional(),
    ogImageOrigin: z.string().optional(),
    ogImagePath: z.string().optional(),
    ogImageAlt: z.string().optional(),
    slug: z.string().optional(),
  }),
});

export const ecommerceSortingValueSchema = z.union([
  z.literal(""),
  z.literal("order=ASC&sort_by=price"),
  z.literal("order=DESC&sort_by=price"),
  z.literal("order=DESC&sort_by=created_at"),
]);

export const siteBlockSettingsSchema = z.object({
  styles: z.record(z.unknown()).optional(),
  categories: z.array(z.string()).optional(),
  logoSvg: z.string().optional(),
});

export const siteEcommerceSortingValueSchema = z.union([
  z.literal(""),
  z.literal("order=ASC&sort_by=price"),
  z.literal("order=DESC&sort_by=price"),
  z.literal("order=DESC&sort_by=created_at"),
  z.literal("order=ASC&sort_by=collection_order"),
]);

export const siteBlockSchema = z.object({
  type: siteBlockTypeSchema,
  settings: siteBlockSettingsSchema.and(z.record(z.unknown())),
  slot: z.string().optional(),
  background: z
    .record(z.unknown())
    .and(
      z.object({
        path: z.string().optional().nullable(),
        origin: z.string().optional().nullable(),
        color: z.string().optional(),
        gradient: siteBackgroundGradientSchema.optional(),
        current: siteBackgroundTypeSchema.optional(),
        "overlay-opacity": z.string().optional(),
        alt: z.string().optional(),
        video: z
          .object({
            videoSrc: z.string(),
            videoThumbnailSrc: z.string(),
          })
          .optional(),
      }),
    )
    .optional(),
  components: z.array(z.string()).optional(),
  zindexes: z.array(z.string()).optional(),
  desktop: z
    .record(z.unknown())
    .and(
      z.object({
        minHeight: z.number().optional(),
      }),
    )
    .optional(),
  mobile: z
    .record(z.unknown())
    .and(
      z.object({
        minHeight: z.number().optional(),
      }),
    )
    .optional(),
  initialBlockId: z.string().optional(),
  locale: z.string().optional(),
  shouldSnapToGuides: z.boolean().optional(),
  shouldSnapToElements: z.boolean().optional(),
  snapRowGap: z.number().optional(),
  snapRowHeight: z.number().optional(),
  isMobileAutoPositioningEnabled: z.boolean().optional(),
  isQuantityPickerEnabled: z.boolean().optional(),
  attachment: z.union([z.literal("unset"), z.literal("fixed")]).optional(),
  htmlId: z.string().optional(),
  isButtonEnabled: z.boolean().optional(),
  buttonDisplay: z.string().optional(),
  aiData: z
    .record(z.unknown())
    .and(
      z.object({
        type: z.string().optional(),
      }),
    )
    .optional(),
  items: z
    .array(
      z.object({
        type: z.string(),
        content: z.string(),
        styles: z.record(z.unknown()),
      }),
    )
    .optional(),
  productSorting: z
    .object({
      enabled: z.boolean(),
      textColor: z.string(),
      sortingOptions: z.array(
        z.object({
          id: z.string(),
          value: siteEcommerceSortingValueSchema,
          label: z.string(),
          isEnabled: z.boolean(),
        }),
      ),
    })
    .optional(),
  productIds: z.array(z.string()).optional(),
  productCategoryId: z.string().optional(),
  isCategoryListEnabled: z.boolean().optional(),
  isFullWidth: z.boolean().optional(),
  isTotalProductCountShown: z.boolean().optional(),
  columnCount: z.number().optional(),
  productsPerPage: z.number().optional(),
  scrollBehaviour: z
    .union([z.literal("pagination"), z.literal("scroll")])
    .optional(),
  animation: animationSchema.optional(),
});

export const sitePageMetaSchema = z.object({
  noindex: z.boolean().optional(),
  title: z.string().optional(),
  password: z.string().optional(),
  ogImageAlt: z.string().optional().nullable(),
  ogImagePath: z.string().optional().nullable(),
  ogImageOrigin: z.string().optional().nullable(),
  passwordDesign: z.string().optional(),
  passwordBackText: z.string().optional(),
  passwordButtonText: z.string().optional(),
  passwordHeadingText: z.string().optional(),
  passwordSubheadingText: z.string().optional(),
  passwordPlaceholderText: z.string().optional(),
});

export const sitePageSchema = z.object({
  type: z.string().optional(),
  locale: z.string().optional(),
  initialPageId: z.string().optional(),
  name: z.string().optional(),
  slug: z.string().optional(),
  date: z.string().optional(),
  isDraft: z.boolean().optional(),
  isScheduled: z.boolean().optional(),
  categories: z.array(z.string()).optional(),
  coverImageAlt: z.string().optional().nullable(),
  coverImagePath: z.string().optional(),
  coverImageOrigin: z.string().optional(),
  minutesToRead: z.string().optional(),
  meta: sitePageMetaSchema.optional(),
  footerSlotIsHidden: z.boolean().optional(),
  pageId: z.string().optional(),
  blocks: z.array(z.string()).optional(),
  productId: z.union([z.string(), z.number()]).optional(),
});

export const navigationTypeSchema = z.union([
  z.literal("Link"),
  z.literal("Page"),
  z.literal("Folder"),
  z.literal("Homepage"),
]);

export const siteNavItemSchema = z.object({
  linkType: navigationTypeSchema.optional(),
  locale: z.string().optional(),
  href: z.string().optional(),
  name: z.string().optional(),
  target: z.string().optional(),
  rel: z.string().optional(),
  innerLinkType: z.string().optional(),
  innerLinkPageId: z.string().optional(),
  subItems: z.array(z.unknown()).optional(),
  navItemId: z.string().optional(),
  linkedPageId: z.string().optional(),
  isHidden: z.boolean().optional(),
});

export const sitePagesSchema = z.record(sitePageSchema);

export const siteBlocksSchema = z.record(siteBlockSchema);

export const siteElementGridFormSchema = siteElementBaseSchema.extend({
  type: z.literal("GridForm"),
  formId: z.string(),
  mobile: siteElementBasePositionSchema.and(
    z.object({
      inputTextSize: z.number(),
      labelTextSize: z.number(),
      submitButtonFontSize: z.number(),
      formElementsVerticalSpacing: z.number(),
    }),
  ),
  desktop: siteElementBasePositionSchema.and(
    z.object({
      inputTextSize: z.number(),
      labelTextSize: z.number(),
      submitButtonFontSize: z.number(),
      formElementsVerticalSpacing: z.number(),
    }),
  ),
  settings: siteElementBaseSettingsSchema.and(
    z.object({
      theme: z.string(),
      schema: z.array(
        z.object({
          id: z.string(),
          svg: z.string(),
          name: z.string(),
          type: z.string(),
          fieldType: z.string(),
          inputLabel: z.string(),
          validation: z.array(z.array(z.string())),
          placeholder: z.string(),
          validationType: z.string(),
          "validation-messages": z.object({
            email: z.string(),
            required: z.string(),
          }),
        }),
      ),
      styles: z.object({
        formSpacing: z.string(),
      }),
      successMessage: z.string(),
    }),
  ),
  formPadding: z.number(),
  inputFillColor: colorSchema,
  inputTextColor: colorSchema,
  labelTextColor: colorSchema,
  formBorderWidth: z.number(),
  innerBackground: z.object({
    color: colorSchema,
    image: z.string(),
    current: z.string(),
  }),
  formBorderRadius: z.number(),
  inputBorderColor: colorSchema,
  inputBorderWidth: z.number(),
  submitButtonData: siteElementGridButtonSchema,
  inputBorderRadius: z.number(),
  formBackgroundColor: z.string(),
  inputFillColorHover: colorSchema,
  inputTextColorHover: colorSchema,
  inputBorderColorHover: colorSchema,
  submitButtonFontColor: colorSchema,
  submitButtonBorderColor: colorSchema,
  submitButtonBorderRadius: z.number(),
  submitButtonFontColorHover: colorSchema,
  submitButtonBackgroundColor: colorSchema,
  submitButtonBorderColorHover: colorSchema,
  submitButtonBackgroundColorHover: colorSchema,
});

export const siteElementSchema = z.union([
  siteElementGridButtonSchema,
  siteElementGridStripeButtonSchema,
  siteElementGridEcommerceButtonSchema,
  siteElementGridMapSchema,
  siteElementGridVideoSchema,
  siteElementGridTextBoxSchema,
  siteElementGridShapeSchema,
  siteElementGridImageSchema,
  siteElementGridInstagramFeedSchema,
  siteElementGridSocialIconsSchema,
  siteElementGridGallerySchema,
  siteElementGridEmbedSchema,
  siteElementSearchBarSchema,
  siteElementGridFormSchema,
]);

export const siteElementsSchema = z.record(siteElementSchema);

export const siteMetaSchema = z.object({
  googleAdsIds: z.array(z.string()).optional(),
  GAId: z.string().optional(),
  shouldAddWWWPrefixToDomain: z.boolean(),
  version: z.number(),
  template: z.string(),
  isFirstLoad: z.boolean().optional(),
  ecommerceStoreId: z.string().optional(),
  demoEcommerceStoreId: z.string().optional(),
  customMeta: z.string().optional(),
  customBodyElements: z.string().optional(),
  customHeadElements: z.string().optional(),
  typographyStylesId: z.string().optional(),
  colorSetId: z.string().optional(),
  buttonSetId: z.string().optional(),
  facebookPixel: z.string().optional(),
  googleAdSense: z.string().optional(),
  googleAdMob: z.string().optional(),
  googleTagManager: z.string().optional(),
  googleAnalytics: z.string().optional(),
  hotjar: z.string().optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  ogImagePath: z.string().optional().nullable(),
  ogImageOrigin: z.string().optional().nullable(),
  ogImageAlt: z.string().optional().nullable(),
  faviconPath: z.string().optional(),
  faviconOrigin: z.string().optional(),
  curatedTemplate: z
    .object({
      link: z.string().optional(),
      author: z.string().optional(),
    })
    .optional(),
  stripePublicApiKey: z.string().optional(),
  whatsAppNumber: z.string().optional(),
  whatsAppMessage: z.string().optional(),
  isCookieBarEnabled: z.boolean().optional(),
  jivoChat: z.string().optional(),
  qa: z.boolean().optional(),
  ecommerceType: z.string().optional(),
  defaultLocale: z.string().optional(),
  isLayout: z.boolean(),
  migrationToLayoutModifications: z
    .object({
      splitSlideshows: z.record(z.any()).optional(),
      imageObjectFitRemoved: z.boolean().optional(),
      unsnappedElementSections: z.record(z.any()).optional(),
    })
    .optional(),
  isPrivateModeActive: z.boolean(),
  aiWebsiteType: z.string().optional(),
  isCustomCodeDisabled: z.boolean().optional(),
  payseraVerificationCode: z.string().optional(),
  isExistingStoreWithDynamicTemplate: z.boolean().optional(),
});

export const siteStylesSchema = z.object({
  h1: siteTypographyStylesSchema,
  h2: siteTypographyStylesSchema,
  h3: siteTypographyStylesSchema,
  h4: siteTypographyStylesSchema,
  h5: siteTypographyStylesSchema,
  h6: siteTypographyStylesSchema,
  "body-small": siteTypographyStylesSchema,
  body: siteTypographyStylesSchema,
  "body-large": siteTypographyStylesSchema,
  "nav-link": siteTypographyStylesSchema,
  font: z.object({
    primary: z.string(),
    secondary: z.string(),
  }),
  "grid-button-primary": siteGridButtonStyleSchema,
  "grid-button-secondary": siteGridButtonStyleSchema,
});

export const siteFormsSchema = z.record(
  z.object({
    token: z.string(),
  }),
);

export const siteLanguageSchema = z.object({
  metaTitle: z.string().optional(),
  name: z.string().optional(),
  homePageId: z.string(),
  pages: sitePagesSchema,
  blocks: siteBlocksSchema,
  elements: siteElementsSchema,
  metaHtmlLanguage: z.string().optional(),
  nav: z.array(siteNavItemSchema),
  isNavHidden: z.boolean(),
  locale: z.string(),
  cookieBannerAcceptText: z.string().optional(),
  cookieBannerDeclineText: z.string().optional(),
  cookieBannerDisclaimer: z.string().optional(),
  blogReadingTimeText: z.string().optional(),
  orderPosition: z.number().optional(),
});

export const siteBlogCategorySchema = z.object({
  name: z.string(),
});

export const siteBlogCategoriesSchema = z.record(siteBlogCategorySchema);

export const siteNavigationBlockSchema = z.record(z.unknown());

export const siteUserSchema = z.record(z.unknown());

export const siteFontSchema = z.object({
  family: z.string(),
  type: z.string(),
  weights: z.array(z.union([z.string(), z.number()])).optional(),
});

export const siteBuilderCompletedStepsSchema = z.object({
  hasChangedLogo: z.boolean().optional(),
});

export const siteLanguagesSchema = z.record(siteLanguageSchema);

export const siteLanguageSwitcherLanguageSchema = z.object({
  country: z.string(),
  flagPath: z.string(),
  href: z.string(),
  isHidden: z.boolean(),
  locale: z.string(),
  name: z.string(),
});

export const upsertRegionCountrySchema = z.object({
  country_code: z.string(),
  provinces: z.array(z.string()),
});

export const siteDataSchema = z.object({
  builderCompletedSteps: siteBuilderCompletedStepsSchema.optional(),
  meta: siteMetaSchema,
  domain: z.string().optional().nullable(),
  freeDomain: z.string().optional(),
  blogCategories: siteBlogCategoriesSchema,
  languages: siteLanguagesSchema,
  styles: siteStylesSchema,
  forms: siteFormsSchema,
  navigationBlock: siteNavigationBlockSchema.optional(),
  user: siteUserSchema.optional(),
  siteId: z.string().optional(),
  fonts: z.array(siteFontSchema).optional(),
  ecommerceShoppingCart: siteEcommerceShoppingCartSchema.optional(),
  ecommerceSeoChanges: z.array(siteEcommerceSeoChangesSchema).optional(),
});
